import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileDownload,
  faRunning,
  faChalkboardTeacher,
  faCalendarAlt,
  faClock,
  faRuler,
  faMountain,
  faHourglassHalf,
  faDollarSign,
} from "@fortawesome/free-solid-svg-icons";
import { InputField, Button } from "@cakrawalajakarta/shared-components";
import { db } from "@lib/firebase";
import {
  collection,
  query,
  where,
  getDoc,
  doc,
  getDocs,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import axios from "axios";
import Iframe from "react-iframe";
import { MandatoryGears } from "../components/MandatoryGears";
import { RaceInfo } from "../components/RaceInfo";
import { Rundown } from "../components/Rundown";
import {
  getJotr2026CategoryMapUrl,
  getJotr2026CategoryRundown,
  getJotr2026CategoryScheduleDetails,
} from "../data/jotr2026Schedule";

const SkeletonLoader = () => (
  <div className="w-full min-h-screen py-20 text-center text-lime-600 flex-col justify-center relative bg-slate-100">
    <div className="relative z-0 container mx-auto px-4">
      <div className="w-full h-[60vh] bg-gray-200 animate-pulse"></div>
      <div className="p-4 space-y-4 mt-8">
        <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
      </div>
    </div>
  </div>
);

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const formatDate = (dateString) => {
  if (!dateString) return "Date not set";
  try {
    let date;
    if (
      typeof dateString === "object" &&
      "toDate" in dateString &&
      typeof dateString.toDate === "function"
    ) {
      date = dateString.toDate();
    } else if (typeof dateString === "object" && "seconds" in dateString) {
      date = new Date(dateString.seconds * 1000);
    } else {
      date = new Date(dateString);
    }
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString("en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
    return "Date not yet set";
  } catch {
    return "Date not yet set";
  }
};

const formatDomesticPrice = (price) => {
  return price != null && Number(price) > 0
    ? `IDR ${Number(price).toLocaleString("en-US")}`
    : "Free";
};

const formatForeignerPrice = (foreignerPrice, fallbackDomesticPrice) => {
  const value =
    foreignerPrice === undefined || foreignerPrice === null
      ? fallbackDomesticPrice
      : foreignerPrice;
  return value != null && Number(value) > 0
    ? `IDR ${Number(value).toLocaleString("en-US")}`
    : "Free";
};

const splitDescription = (description) => {
  if (!description) return ["No description available.", ""];
  const words = description.split(" ");
  const midPoint = Math.ceil(words.length / 2);
  const firstHalf = words.slice(0, midPoint).join(" ");
  const secondHalf = words.slice(midPoint).join(" ");
  return [firstHalf || "No description available.", secondHalf];
};

const isRegistrationOpenStatus = (status) => {
  if (!status) return false;
  const normalized = status.trim().toLowerCase();
  return [
    "registration open",
    "open",
    "pendaftaran dibuka",
    "registrasi dibuka",
  ].includes(normalized);
};

const getCategoryIcons = (categoryName, distance) => {
  const normName = categoryName.trim().toUpperCase();

  if (normName.includes("FUN TRAIL") || distance == 5) {
    return {
      icon1Url: "/icons/utmb.png",
      icon2Url: "/icons/itra0.svg",
      icon3Url: null
    };
  } else if (normName.includes("CIANJUR TRAIL RUN") || distance == 10) {
    return {
      icon1Url: "/icons/utmb.png",
      icon2Url: "/icons/itra0.svg",
      icon3Url: null
    };
  } else if (normName.includes("HM TRAIL") || distance == 21) {
    return {
      icon1Url: "/icons/utmb.png",
      icon2Url: "/icons/utmb20.png",
      icon3Url: "/icons/itra1.png"
    };
  } else if (normName.includes("MOUNTAIN MARATHON") || distance == 42) {
    return {
      icon1Url: "/icons/utmb.png",
      icon2Url: "/icons/utmb50.png",
      icon3Url: "/icons/itra3.png"
    };
  } else if (normName.includes("ULTRA TRAIL") || distance == 75) {
    return {
      icon1Url: "/icons/utmb.png",
      icon2Url: "/icons/utmb100.svg",
      icon3Url: "/icons/itra4.png"
    };
  }

  return { icon1Url: null, icon2Url: null, icon3Url: null };
};


// Fungsi helper untuk check tanggal (sama seperti di SectionKategori)
const isEventInFuture = (eventDateStr) => {
  if (!eventDateStr) return false;
  try {
    const eventDate = new Date(eventDateStr);
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Reset jam untuk perbandingan harian
    eventDate.setHours(0, 0, 0, 0);
    return eventDate > now;
  } catch {
    return false;
  }
};

export const KegiatanDetail = React.memo(() => {
  const { slug, distance } = useParams();
  // Default to JOTR2026 if slug is not provided (for short URL /JOTR2026/:distance)
  const eventSlug = slug || "JOTR2026";
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [event, setEvent] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewModeState] = useState(() => {
    return localStorage.getItem("jotr_view_mode") || "map";
  });

  const setViewMode = (mode) => {
    setViewModeState(mode);
    localStorage.setItem("jotr_view_mode", mode);
  };
  const [collectionName, setCollectionName] = useState(null);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [snapToken, setSnapToken] = useState(null);
  const [isSnapReady, setIsSnapReady] = useState(false);

  const fetchCollectionName = useCallback(async (eventId) => {
    try {
      const eventRef = doc(db, "events", eventId);
      const eventDoc = await getDoc(eventRef);
      if (eventDoc.exists() && eventDoc.data().collectionName) {
        return eventDoc.data().collectionName;
      }

      const contentRef = doc(db, "semuaKegiatan", eventId);
      const contentDoc = await getDoc(contentRef);
      if (contentDoc.exists() && contentDoc.data().collectionName) {
        return contentDoc.data().collectionName;
      }

      return `pendaftaran${eventId}`;
    } catch {
      return `pendaftaran${eventId}`;
    }
  }, []);

  const fetchEventData = useCallback(async () => {
    try {
      const eventDoc = await getDoc(doc(db, "semuaKegiatan", eventSlug));
      if (!eventDoc.exists()) {
        setError("Event not found");
        setLoading(false);
        return null;
      }

      const eventData = { id: eventDoc.id, ...eventDoc.data() };
      setEvent(eventData);

      // Ambil status dari level event
      const eventStatus = eventData.status || "Registration Closed";
      const isEventOpen = isRegistrationOpenStatus(eventStatus);

      if (distance && eventData.categories) {
        let category;
        if (distance === "20-group") {
          category = eventData.categories.find(
            (cat) => cat.categoryName.trim() === "Mix Group (20K)"
          );
        } else if (distance === "7-fun") {
          category = eventData.categories.find(
            (cat) => cat.categoryName.trim() === "Fun Trail (7K)"
          );
        } else {
          const parsedDistance = parseFloat(distance);
          category = eventData.categories.find(
            (cat) =>
              cat.distance === parsedDistance &&
              cat.categoryName.trim() !== "Fun Trail (7K)"
          );
        }
        if (category) {
          const normalizedCategoryName = category.categoryName.trim();
          const scheduleDetails =
            eventSlug === "JOTR2026"
              ? getJotr2026CategoryScheduleDetails(category)
              : {};
          const categoryMapUrl =
            eventSlug === "JOTR2026"
              ? getJotr2026CategoryMapUrl(category)
              : null;
          const resolvedCategory = {
            ...category,
            ...scheduleDetails,
            ...(categoryMapUrl ? { mapUrl: categoryMapUrl } : {}),
            categoryName: normalizedCategoryName,
            isRegistrationOpen: category.isRegistrationOpen === true || (isEventOpen && category.isRegistrationOpen !== false),
          };
          setSelectedCategory(resolvedCategory);
          setLoading(false);
          return { ...eventData, _resolvedCategory: resolvedCategory };
        } else {
          setError(`Category for ${distance} not found`);
          console.error("[ERROR] Category not found for distance:", distance);
        }
      } else {
        setError("Invalid distance parameter");
        console.error("[ERROR] Invalid distance parameter:", distance);
      }

      setLoading(false);
      return eventData;
    } catch (err) {
      setError(err.message);
      console.error("[ERROR] Error fetching event data:", err);
      setLoading(false);
      return null;
    }
  }, [eventSlug, distance]);

  const checkRegistration = useCallback(
    async (collectionName, eventId, currentCategory) => {
      if (!user || !eventId || !collectionName)
        return { isRegistered: false, status: null, token: null };

      try {
        const q = query(
          collection(db, collectionName),
          where("uid", "==", user.uid),
          where("eventId", "==", eventId)
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const selectedCategoryName = currentCategory?.categoryName?.trim();
          const selectedDistance = currentCategory?.distance;
          const isSameCategory = (data) => {
            const registeredCategory = data.kategori?.trim();
            const registeredDistance = Number(data.distance);
            return (
              !selectedCategoryName ||
              registeredCategory === selectedCategoryName ||
              (!Number.isNaN(registeredDistance) &&
                Number(selectedDistance) === registeredDistance)
            );
          };
          const activeDoc = querySnapshot.docs.find((doc) => {
            const data = doc.data();
            const status = data.transaction_status || "unknown";
            const adminStatus = data.administration_status || "pending";

            if (!isSameCategory(data)) return false;
            if (adminStatus === "rejected") return false;
            if (["expire", "cancel", "deny", "failure"].includes(status)) return false;

            return (
              adminStatus === "pending" ||
              adminStatus === "qualified" ||
              ["pending", "settlement", "capture"].includes(status)
            );
          });

          if (!activeDoc) {
            setRegistrationStatus("not_registered");
            setTransactionStatus(null);
            setSnapToken(null);
            return { isRegistered: false, status: null, token: null };
          }

          const doc = activeDoc;
          const data = doc.data();
          const status = data.transaction_status || "unknown";
          const paymentType = data.payment_type || "unknown";
          const harga = data.harga || 0;

          if (
            status === "settlement" ||
            (status === "pending" && paymentType !== "free")
          ) {
            
            // Explicitly check for failure statuses first to clean up
             if (["expire", "cancel", "deny", "failure"].includes(status)) {
                // Do NOT delete the registration document; keep admin approval state.
                // Clear stale token so user can recreate a new transaction.
                await updateDoc(doc.ref, {
                  transaction_status: status,
                  snapToken: null,
                });
                setRegistrationStatus("registered");
                setTransactionStatus(status);
                setSnapToken(null);
                return { isRegistered: true, status, token: null };
            }

            setRegistrationStatus("registered");
            setTransactionStatus(status);
            setSnapToken(data.snapToken || null);

            if (status === "pending" && data.snapToken && data.orderId) {
              const response = await axios.get(
                `${import.meta.env.VITE_API_BASE_URL}/api/payment/status/${
                  data.orderId
                }`,
                { params: { eventId } }
              );
              const updatedStatus = response.data.transaction_status;
              if (updatedStatus !== status) {
                if (
                  updatedStatus === "pending" ||
                  updatedStatus === "settlement"
                ) {
                  await updateDoc(doc.ref, {
                    transaction_status: updatedStatus,
                  });
                  setTransactionStatus(updatedStatus);
                  return {
                    isRegistered: true,
                    status: updatedStatus,
                    token: data.snapToken,
                  };
                } else {
                  // Jika status menjadi expire/cancel/deny, baru hapus
                  // Do NOT delete; keep document and clear token to allow retry payment.
                  await updateDoc(doc.ref, {
                    transaction_status: updatedStatus,
                    snapToken: null,
                  });
                  setRegistrationStatus("registered");
                  setTransactionStatus(updatedStatus);
                  setSnapToken(null);
                  return { isRegistered: true, status: updatedStatus, token: null };
                }
              }
            } else if (
              data.administration_status === "pending" ||
              data.administration_status === "qualified"
            ) {
              // VALID STATE: Menunggu verifikasi admin atau sudah disetujui (siap bayar)
              setRegistrationStatus("registered");
              setTransactionStatus(status);
              // snapToken mungkin null jika belum ada transaksi, itu wajar untuk flow ini
              setSnapToken(data.snapToken || null);
              return { isRegistered: true, status, token: data.snapToken || null };
            }

            return { isRegistered: true, status, token: data.snapToken };
          } else {
             // If status is expire/cancel/deny/failure in the 'else' block (or not caught above), delete it.
             if (["expire", "cancel", "deny", "failure"].includes(status)) {
                await updateDoc(doc.ref, {
                  transaction_status: status,
                  snapToken: null,
                });
                setRegistrationStatus("registered");
                setTransactionStatus(status);
                setSnapToken(null);
                return { isRegistered: true, status, token: null };
            }

            // Check again for administration status before deleting
            if (
              data.administration_status === "pending" ||
              data.administration_status === "qualified"
            ) {
              setRegistrationStatus("registered");
              setTransactionStatus(status);
              setSnapToken(data.snapToken || null);
              return { isRegistered: true, status, token: data.snapToken || null };
            }

            // For non-approved registrations, keep current behavior conservative,
            // but avoid deleting documents from client-side.
            await updateDoc(doc.ref, {
              snapToken: null,
            });
            setRegistrationStatus("registered");
            setTransactionStatus(status);
            setSnapToken(null);
            return { isRegistered: true, status, token: null };
          }
        } else {
          setRegistrationStatus("not_registered");
          return { isRegistered: false, status: null, token: null };
        }
      } catch (err) {
        // Don't set page-level error for registration check failures
        // User can still view the page, just registration status is unknown
        console.error("[ERROR] Failed to check registration status:", err);
        return { isRegistered: false, status: null, token: null };
      }
    },
    [user]
  );

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      const eventData = await fetchEventData();
      if (!eventData || !mounted) return;

      if (eventData.eventId) {
        try {
          const fetchedCollectionName = await fetchCollectionName(eventData.eventId);

          if (mounted) {
            setCollectionName(fetchedCollectionName);
            if (fetchedCollectionName && user) {
              const { isRegistered, status, token } = await checkRegistration(
                fetchedCollectionName,
                eventData.eventId,
                eventData._resolvedCategory
              );
              setRegistrationStatus(
                isRegistered ? "registered" : "not_registered"
              );
              setTransactionStatus(status);
              setSnapToken(token);
            }
          }
        } catch {
          if (mounted) setCollectionName(null);
        }
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
  }, [fetchEventData, fetchCollectionName, user]);

  /* useEffect(() => {
    if (selectedCategory) {
      // Jika mode saat ini adalah 'strava' tapi rutenya tidak ada, balikkan ke 'map'
      if (viewMode === "strava" && !selectedCategory.stravaRouteId) {
        setViewMode("map");
      }
    }
  }, [selectedCategory, viewMode]);

  useEffect(() => {
    if (viewMode === "strava" && selectedCategory?.stravaRouteId) {
      const scriptId = "strava-embed-script";
      let script = document.getElementById(scriptId);
      if (script) {
        document.body.removeChild(script);
      }
      const newScript = document.createElement("script");
      newScript.id = scriptId;
      newScript.src = "https://strava-embeds.com/embed.js";
      newScript.async = true;
      document.body.appendChild(newScript);
    }
  }, [viewMode, selectedCategory?.stravaRouteId]); */

  useEffect(() => {
    const checkSnap = setInterval(() => {
      if (window.snap) {
        setIsSnapReady(true);
        clearInterval(checkSnap);
      }
    }, 100);
    return () => clearInterval(checkSnap);
  }, []);

  const handleBayar = useCallback(async () => {
    if (!isSnapReady || !window.snap) {
      showNotification("error", "Midtrans is not ready. Please try again.");
      return;
    }

    if (!snapToken) {
      showNotification("error", "Payment token not found.");
      return;
    }

    try {
      window.snap.pay(snapToken, {
        onSuccess: async () => {
          showNotification("success", "Payment successful!");
          const { status } = await checkRegistration(
            collectionName,
            event.eventId,
            selectedCategory
          );
          setTransactionStatus(status);
          navigate("/myregistration");
        },
        onPending: async () => {
          showNotification("warning", "Payment is being processed.");
          const { status } = await checkRegistration(
            collectionName,
            event.eventId,
            selectedCategory
          );
          setTransactionStatus(status);
          navigate("/myregistration");
        },
        onError: () => {
          showNotification("error", "Payment failed. Please try again.");
        },
        onClose: () => {},
      });
    } catch {
      showNotification("error", "Failed to process payment.");
    }
  }, [
    isSnapReady,
    snapToken,
    collectionName,
    event,
    showNotification,
    navigate,
    checkRegistration,
  ]);

  const handleRegister = useCallback(
    (category) => {
      if (
        !event ||
        !event.title ||
        (category.price == null && event.price == null) ||
        !event.eventId
      ) {
        showNotification(
          "error",
          "Incomplete event data. Please try again."
        );
        return;
      }

      if (!user) {
        showNotification(
          "error",
          "Please login first to register."
        );
        navigate("/login");
        return;
      }

      const resolvedCollectionName =
        collectionName || event.collectionName || `pendaftaran${event.eventId}`;

      if (!resolvedCollectionName) {
        showNotification(
          "error",
          "Failed to load event data. Please try again."
        );
        return;
      }

      if (!category.isRegistrationOpen) {
        showNotification(
          "error",
          "Registration for this category is closed."
        );
        return;
      }

      const title =
        event.type === "seminar"
          ? event.title
          : `${event.title} - ${category.categoryName || ""}`;
      const path = `/form/${event.slug}`;

      if (registrationStatus === "registered") {
        if (transactionStatus === "settlement") {
          showNotification("info", "You are already registered for this event.");
        } else if (transactionStatus === "pending") {
          handleBayar();
        } else {
          // Status qualified/unknown - redirect to myregistration to complete payment
          showNotification("info", "You are already registered. Please complete the payment.");
          navigate("/myregistration");
        }
        return;
      }

      navigate(path, {
        state: {
          title,
          price: category.price ?? event.price ?? 0,
          foreignerPrice:
            category.foreignerPrice ?? category.price ?? event.price ?? 0,
          eventId: event.eventId,
          categoryName: category.categoryName,
          collectionName: resolvedCollectionName,
        },
      });
    },
    [
      event,
      user,
      collectionName,
      registrationStatus,
      transactionStatus,
      showNotification,
      navigate,
      handleBayar,
    ]
  );

  const handleExportGpx = useCallback(
    debounce(async (gpxFileUrl, selectedCategory, event) => {
      try {
        const response = await fetch(gpxFileUrl, { mode: "cors" });
        const gpxText = await response.text();
        const blob = new Blob([gpxText], { type: "application/gpx+xml" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const fileName =
          event.type === "seminar"
            ? `${event.title.toLowerCase().replace(/\s+/g, "-")}.gpx`
            : `${event.title.toLowerCase().replace(/\s+/g, "-")}-${
                selectedCategory.distance
              }km.gpx`;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch {
        showNotification("error", "Failed to download GPX file.");
      }
    }, 500),
    []
  );

  const isRegistrationOpen = useMemo(() => {
    const eventStatus = event?.status || "Registration Closed";
    const isEventOpen = isRegistrationOpenStatus(eventStatus);
    // Using isRegistrationOpen from selectedCategory
    return selectedCategory?.isRegistrationOpen === true || (isEventOpen && selectedCategory?.isRegistrationOpen !== false);
  }, [event, selectedCategory]);

  const getEventIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "trail_running":
        return { icon: faRunning, size: "text-5xl" };
      case "seminar":
        return { icon: faChalkboardTeacher, size: "text-4xl" };
      default:
        return { icon: faCalendarAlt, size: "text-4xl" };
    }
  };

  if (loading) {
    return <SkeletonLoader />;
  }

  if (error || !event || !selectedCategory) {
    return (
      <section className="w-full min-h-screen py-20 text-center text-lime-600 flex-col justify-center relative bg-slate-100">
        <motion.h2
          className="text-xl sm:text-2xl md:text-3xl font-normal uppercase mb-4 text-lime-600"
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          EVENT
        </motion.h2>
        <motion.p
          className="text-4xl sm:text-5xl md:text-7xl font-black mx-auto mb-16 text-lime-600 px-4"
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {error || "NOT FOUND"}
        </motion.p>
        <Link to="/kegiatan" className="text-lime-600 underline">
          Back to Event List
        </Link>
      </section>
    );
  }

  const categoryIcons = getCategoryIcons(
    selectedCategory.categoryName,
    selectedCategory.distance
  );

  return (
    <section className="w-full min-h-screen text-lime-600 relative bg-white">
      <div
        className="relative w-full h-[60vh] flex items-center justify-center"
        style={{
          backgroundImage: 'url("/bg/bghero11.jpg")',
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-lime-600/100 to-lime-600/20 flex flex-col items-center justify-center gap-6">
          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          >
            <FontAwesomeIcon
              icon={getEventIcon(event.type).icon}
              className={`text-white ${getEventIcon(event.type).size} z-10`}
              aria-label={`Icon for ${
                event.type === "trail_running" ? "Trail Running" : "Seminar"
              }`}
            />
          </motion.div>
          <div className="z-0 text-center">
            <motion.h1
              className="text-3xl md:text-5xl font-black uppercase text-white px-4"
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                delay: 0.2,
                ease: [0.4, 0, 0.2, 1],
              }}
            >
              {selectedCategory.categoryName || event.title}
            </motion.h1>
            <motion.div
              className="flex justify-center gap-4 mt-6"
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                delay: 0.3,
                ease: [0.4, 0, 0.2, 1],
              }}
            >
              {[categoryIcons.icon1Url, categoryIcons.icon2Url, categoryIcons.icon3Url]
                .filter(Boolean)
                .sort((a, b) => (a.includes("itra") ? 0 : 1) - (b.includes("itra") ? 0 : 1))
                .map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt="Category badge"
                    className="h-6 object-contain"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ))}
            </motion.div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <motion.div
          className="space-y-8 max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          {(event.type === "trail_running" || event.type === "seminar") && (
            <div>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className="text-2xl text-lime-600"
                  />
                  <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                    Date
                  </span>
                  <div className="text-sm text-gray-700">
                    {formatDate(selectedCategory.date)}
                  </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
                  <FontAwesomeIcon
                    icon={faClock}
                    className="text-2xl text-lime-600"
                  />
                  <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                    Start
                  </span>
                  <div className="text-sm text-gray-700">
                    {selectedCategory.time || "-"}
                  </div>
                </div>
                {event.type === "trail_running" && (
                  <>
                    <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
                      <FontAwesomeIcon
                        icon={faRuler}
                        className="text-2xl text-lime-600"
                      />
                      <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                        Distance
                      </span>
                      <div className="text-sm text-gray-700">
                        {selectedCategory.distance != null
                          ? `${selectedCategory.distance} KM`
                          : "-"}
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
                      <FontAwesomeIcon
                        icon={faMountain}
                        className="text-2xl text-lime-600"
                      />
                      <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                        Elevation Gain
                      </span>
                      <div className="text-sm text-gray-700">
                        {selectedCategory.elevationGain != null
                          ? `${selectedCategory.elevationGain} M`
                          : "-"}
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
                      <FontAwesomeIcon
                        icon={faHourglassHalf}
                        className="text-2xl text-lime-600"
                      />
                      <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                        COT
                      </span>
                      <div className="text-sm text-gray-700">
                        {selectedCategory.cot != null
                          ? `${selectedCategory.cot} Hours`
                          : "-"}
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
                      <FontAwesomeIcon
                        icon={faDollarSign}
                        className="text-2xl text-lime-600"
                      />
                      <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                        REG FEE
                      </span>
                      <div className="text-sm font-semibold text-lime-700 text-center">
                        {formatDomesticPrice(selectedCategory.price)}
                      </div>
                     <div className="text-xs font-medium text-slate-500 text-center">
                        Foreigner: {formatForeignerPrice(
                          selectedCategory.foreignerPrice,
                          selectedCategory.price
                        )}
                      </div>
                    </div>
                  </>
                )}
                {event.type === "seminar" && (
                  <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
                    <FontAwesomeIcon
                      icon={faDollarSign}
                      className="text-2xl text-lime-600"
                    />
                    <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                      REG FEE
                    </span>
                    <div className="text-sm font-semibold text-lime-700 text-center">
                      {formatDomesticPrice(selectedCategory.price)}
                    </div>
                   {/*  <div className="text-xs font-medium text-slate-500 text-center">
                      Foreigner: {formatForeignerPrice(
                        selectedCategory.foreignerPrice,
                        selectedCategory.price
                      )}
                    </div> */}
                  </div>
                )}
              </div>
            </div>
          )}

          {event.eventId !== "ROADTOJOTR2026" && (
            <RaceInfo 
              categoryName={selectedCategory.categoryName}
              qualification={selectedCategory.qualification}
              raceInclusions={selectedCategory.raceInclusions}
            />
          )}

          <div>
            <div className="mt-8">
              {registrationStatus === "registered" ? (
                transactionStatus === "settlement" ? (
                  <Button
                    variant="primary"
                    className="w-full uppercase opacity-80 cursor-default"
                    disabled
                  >
                    YOU ARE ALREADY REGISTERED
                  </Button>
                ) : transactionStatus === "pending" ? (
                  <Button
                    onClick={handleBayar}
                    variant="primary"
                    className="w-full uppercase"
                  >
                    PAY NOW
                  </Button>
                ) : (
                  <Button
                    onClick={() => navigate("/myregistration")}
                    variant="primary"
                    className="w-full uppercase"
                  >
                    COMPLETE PAYMENT
                  </Button>
                )
              ) : (
                <Button
                  onClick={() => handleRegister(selectedCategory)}
                  variant="primary"
                  className="w-full uppercase"
                  disabled={!isRegistrationOpen}
                >
                  {isRegistrationOpen
                    ? "REGISTER THIS CATEGORY!"
                    : "REGISTRATION CLOSED"}
                </Button>
              )}
            </div>
          </div>

          {event.type === "trail_running" && (
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="mb-4">
                <div className="hidden md:flex space-x-0 rounded-t-xl overflow-hidden">
                  {/* {selectedCategory.stravaRouteId && (
                    <button
                      className={`flex-1 text-center uppercase px-4 h-10 transition-colors duration-300 ${
                        viewMode === "strava"
                          ? "bg-lime-600 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      } rounded-tl-xl`}
                      onClick={() => setViewMode("strava")}
                    >
                      STRAVA MAP
                    </button>
                  )} */}
                  <button
                    className={`flex-1 text-center uppercase px-4 h-10 transition-colors duration-300 ${
                      viewMode === "map"
                        ? "bg-lime-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    } ${!selectedCategory.stravaRouteId ? "rounded-tl-xl" : ""}`}
                    onClick={() => setViewMode("map")}
                  >
                    COURSE MAP
                  </button>
                  <button
                    className={`flex-1 text-center uppercase px-4 h-10 transition-colors duration-300 ${
                      viewMode === "rundown"
                        ? "bg-lime-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    onClick={() => setViewMode("rundown")}
                  >
                    RUNDOWN
                  </button>
                  {event.eventId !== "ROADTOJOTR2026" && (
                    <button
                      className={`flex-1 text-center uppercase px-4 h-10 transition-colors duration-300 ${
                        viewMode === "mandatory"
                          ? "bg-lime-600 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      } ${viewMode === "mandatory" ? "rounded-tr-xl" : ""}`}
                      onClick={() => setViewMode("mandatory")}
                    >
                      MANDATORY GEARS
                    </button>
                  )}
                </div>
                <div className="md:hidden flex flex-col divide-y divide-gray-300 border-b border-gray-300 rounded-t-xl overflow-hidden">
                  {/* {selectedCategory.stravaRouteId && (
                    <button
                      className={`w-full uppercase px-4 h-10 block transition-colors duration-300 ${
                        viewMode === "strava"
                          ? "bg-lime-600 text-white"
                          : "bg-white text-gray-600"
                      }`}
                      onClick={() => setViewMode("strava")}
                    >
                      STRAVA MAP
                    </button>
                  )} */}
                  <button
                    className={`w-full uppercase px-4 h-10 block transition-colors duration-300 ${
                      viewMode === "map"
                        ? "bg-lime-600 text-white"
                        : "bg-white text-gray-600"
                    }`}
                    onClick={() => setViewMode("map")}
                  >
                    COURSE MAP
                  </button>
                  <button
                    className={`w-full uppercase px-4 h-10 block transition-colors duration-300 ${
                      viewMode === "rundown"
                        ? "bg-lime-600 text-white"
                        : "bg-white text-gray-600"
                    }`}
                    onClick={() => setViewMode("rundown")}
                  >
                    RUNDOWN
                  </button>
                  {event.eventId !== "ROADTOJOTR2026" && (
                    <button
                      className={`w-full uppercase px-4 h-10 block transition-colors duration-300 ${
                        viewMode === "mandatory"
                          ? "bg-lime-600 text-white"
                          : "bg-white text-gray-600"
                      }`}
                      onClick={() => setViewMode("mandatory")}
                    >
                      MANDATORY GEARS
                    </button>
                  )}
                </div>
              </div>
              {/* {viewMode === "strava" && selectedCategory.stravaRouteId && (
                <div className="pb-4">
                  <div 
                    className="relative w-full min-h-[765px] mt-4"
                    dangerouslySetInnerHTML={{
                      __html: `<div class="strava-embed-placeholder" data-embed-type="route" data-embed-id="${selectedCategory.stravaRouteId}" data-full-width="true" data-style="standard" data-terrain="2d" data-club-id="1097470"></div>`
                    }}
                  />
                </div>
              )} */}
              {viewMode === "map" && selectedCategory.mapUrl && (
                <div className="pb-4">
                  <div className="relative w-full h-[600px] overflow-hidden mt-4">
                    <Iframe
                      url={selectedCategory.mapUrl}
                      width="100%"
                      height="600px"
                      display="block"
                      position="relative"
                    />
                  </div>
                  {selectedCategory.description && (
                    <p className="text-lg px-4 leading-relaxed text-gray-700 text-center mb-4">
                      {selectedCategory.description}
                    </p>
                  )}
                  {selectedCategory.gpxFileUrl && (
                    <div className="text-center px-4 mt-4">
                      <Button
                        variant="primary"
                        onClick={() =>
                          handleExportGpx(
                            selectedCategory.gpxFileUrl,
                            selectedCategory,
                            event
                          )
                        }
                        className="gap-2"
                        title="Download route GPX file"
                      >
                        <FontAwesomeIcon icon={faFileDownload} />
                        <span>Download GPX</span>
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {viewMode === "rundown" && (
                <Rundown
                  categoryName={selectedCategory.categoryName}
                  rundown={
                    eventSlug === "JOTR2026"
                      ? getJotr2026CategoryRundown(selectedCategory)
                      : selectedCategory.rundown
                  }
                />
              )}
              {viewMode === "mandatory" && event.eventId !== "ROADTOJOTR2026" && (
                <div>
                  <MandatoryGears
                    categoryName={selectedCategory.categoryName}
                    mandatoryGears={selectedCategory.mandatoryGears}
                  />
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
});
